import { useEffect, useMemo, useState } from "react";
import type { DeskFeed, FallbackTier, PrecipNowcast, ReservoirObservation } from "@chonburi/shared";
import { API_BASE } from "../lib/apiBase";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useBriefSurface, type BriefFeedMeta } from "../hooks/useBriefSurface";
import { useDeskTranslation } from "../hooks/useDeskTranslation";
import { DialogClose } from "./DialogClose";
import { DeskCensus } from "./DeskCensus";
import { DeskStateStrip } from "./DeskStateStrip";
import {
  ictTime,
  thaiDate,
  type AirBriefInput,
  type FeedHealthInput,
  type IncidentBriefFeed,
} from "../lib/mayorBrief";
import {
  buildDesk,
  deskContext,
  deskState,
  deskText,
  LANG_LABEL,
  type DeskFeedState,
  type DeskItem,
} from "../lib/mayorDesk";
import type { MarineSnapshot } from "./CoastalBrief";
import type { TideSnapshot } from "./TidePanel";
import type { LensId } from "../map/presets";

/**
 * The Mayor's Desk — one click, one screen, most important first.
 *
 * A bento, not a buffet: the state's four conditions across the top, the one
 * headline that matters most beside the coverage the mayor would never have
 * found himself, the rest of the Thai and English coverage below, and the
 * census of what the desk could not show. Every compartment holds real
 * content, and a compartment with nothing to say is not rendered.
 *
 * The desk is a morning read, so it is fetched once when it opens — not
 * polled. The API caches the feed for a quarter of an hour and keeps every
 * translation for a day.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Jump to the lens where a condition's evidence lives. */
  onOpenLens: (lens: LensId) => void;
  /** The same raw feeds the mayor brief reads, so the two never disagree. */
  air: AirBriefInput | null;
  rain: PrecipNowcast | null;
  rainMeta: BriefFeedMeta;
  reservoirs: ReservoirObservation[];
  reservoirMeta: BriefFeedMeta;
  marine: MarineSnapshot | null;
  marineMeta: BriefFeedMeta;
  tides: TideSnapshot | null;
  tideMeta: BriefFeedMeta;
  incidentFeeds: IncidentBriefFeed[];
  feeds: FeedHealthInput;
}

type DeskLoad =
  | { status: "loading" }
  | { status: "ready"; feed: DeskFeed }
  | { status: "failed" };

const COPY_IDLE = "idle";
type CopyState = typeof COPY_IDLE | "copied" | "failed";

/** A story's publication date, in the reader's locale and time. */
function publishedAt(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  return new Date(t).toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** The headline the mayor reads, with the publisher's own words kept under it. */
function StoryLines({ item, lead }: { item: DeskItem; lead?: boolean }) {
  return (
    <>
      <h3 className={`desk-story-headline${lead ? " desk-story-headline--lead" : ""}`}>
        {item.headline}
      </h3>
      {item.original && (
        <p className="desk-story-original" lang={item.lang}>
          {item.original}
        </p>
      )}
    </>
  );
}

/** The publisher, the date, and the language the publisher wrote in. */
function StoryProvenance({ item }: { item: DeskItem }) {
  return (
    <p className="desk-story-provenance mono">
      <span className="desk-lang" title={LANG_LABEL[item.lang].en}>
        {LANG_LABEL[item.lang].short}
      </span>
      <span className="desk-story-source">{item.source}</span>
      <span>{publishedAt(item.publishedAt)}</span>
      {item.machine && (
        <span
          className="desk-machine"
          title={`แปลอัตโนมัติจาก${LANG_LABEL[item.lang].th} — ต้นฉบับอยู่ข้างบน / Machine-translated from ${LANG_LABEL[item.lang].en}; the original is above`}
        >
          แปลอัตโนมัติ · machine
        </span>
      )}
    </p>
  );
}

export function MayorsDesk({
  open,
  onClose,
  onOpenLens,
  air, rain, rainMeta, reservoirs, reservoirMeta,
  marine, marineMeta, tides, tideMeta, incidentFeeds, feeds,
}: Props) {
  const containerRef = useFocusTrap(open);
  const [load, setLoad] = useState<DeskLoad>({ status: "loading" });
  const [copyState, setCopyState] = useState<CopyState>(COPY_IDLE);
  const [copiedWhich, setCopiedWhich] = useState<"th" | "en">("th");

  // The four conditions, shaped by the shared hook so the desk and the
  // brief cannot disagree about the same dam or the same wave.
  const { tiles, actions, now } = useBriefSurface({
    air, rain, rainMeta, reservoirs, reservoirMeta,
    marine, marineMeta, tides, tideMeta, incidentFeeds, feeds,
  });

  // The desk is fetched when it opens, not polled: it is a morning read and
  // the API already keeps the feed for a quarter of an hour.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoad({ status: "loading" });
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/news/desk`);
        if (!res.ok) throw new Error(`desk ${res.status}`);
        const json = (await res.json()) as DeskFeed;
        if (!cancelled) setLoad({ status: "ready", feed: json });
      } catch {
        if (!cancelled) setLoad({ status: "failed" });
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (copyState !== "copied") return;
    const timer = window.setTimeout(() => setCopyState(COPY_IDLE), 2500);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const feed = load.status === "ready" ? load.feed : null;

  // The API could not render some foreign headlines — its address is
  // throttled by the public translation services — so this browser tries,
  // after the desk is already on screen, and the world compartment fills
  // in when it succeeds.
  const translation = useDeskTranslation(feed, now);

  const feedState: DeskFeedState | null = useMemo(() => {
    if (!feed) return null;
    const f = feed;
    const done = translation.byLanguage;
    const doneTotal = Object.values(done).reduce((sum, n) => sum + n, 0);
    // What is still missing is the API's own count, less what this browser
    // managed — a number, not a shrug.
    const unreadable = Math.max(0, (f.unreadable ?? 0) - doneTotal);
    // The languages are the API's, less the ones this browser emptied. A
    // language with no known split is kept: naming a gap that has closed is
    // better than hiding one that is still open.
    const untranslatedLanguages = (f.untranslatedLanguages ?? []).filter((l) => {
      const total = f.untranslatedByLanguage?.[l];
      if (total == null) return true;
      return total - (done[l] ?? 0) > 0;
    });
    return {
      tier: f.meta.fallbackTier as FallbackTier,
      ageMinutes: f.meta.ageMinutes,
      source: f.meta.source,
      languages: f.languages ?? [],
      emptyLanguages: f.emptyLanguages ?? [],
      // A feed that did not answer is named as broken. An older API did not
      // have the field, and an older API never failed this way — so an
      // absence of the field means there is nothing failed to report.
      failedLanguages: f.failedLanguages ?? [],
      untranslatedLanguages,
      unreadable,
      translated: f.translated ?? false,
    };
  }, [feed, translation.byLanguage]);

  const desk = useMemo(
    () => buildDesk(translation.stories, feedState, now),
    [translation.stories, feedState, now],
  );

  const cells = useMemo(() => deskState(tiles), [tiles]);
  const text = useMemo(() => deskText(desk, tiles, now), [desk, tiles, now]);
  const context = useMemo(
    () => (desk.lead ? deskContext(desk.lead, now) : { th: "", en: "" }),
    [desk.lead, now],
  );

  if (!open) return null;

  const copy = async (which: "th" | "en") => {
    setCopiedWhich(which);
    try {
      await navigator.clipboard.writeText(which === "th" ? text.th : text.en);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const goToEvidence = (evidence: string) => {
    onClose();
    onOpenLens(evidence as LensId);
  };

  const loading = load.status === "loading";
  const failed = load.status === "failed";
  const hasStories = desk.items.length > 0;
  const machineCount = desk.items.filter((i) => i.machine).length;

  return (
    <div className="modal-backdrop desk-backdrop" onClick={onClose}>
      <div
        ref={containerRef}
        className="desk"
        role="dialog"
        aria-modal="true"
        aria-label="โต๊ะนายกเมือง / Mayor's desk"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="desk-head">
          <div className="desk-identity">
            <h2>โต๊ะนายกเมือง <span>Mayor's desk</span></h2>
            <p className="desk-date mono">
              {thaiDate(now.toISOString())} · {ictTime(now)} ICT
            </p>
          </div>
          <div className="desk-actions">
            <button type="button" onClick={() => copy("th")} className="desk-primary">
              {copyState === "copied" && copiedWhich === "th" ? "คัดลอกแล้ว · Copied" : "คัดลอกส่ง LINE"}
            </button>
            <button type="button" onClick={() => copy("en")}>
              {copyState === "copied" && copiedWhich === "en" ? "Copied" : "Copy English"}
            </button>
          </div>
          <DialogClose onClose={onClose} label="ปิดโต๊ะนายกเมือง / Close mayor's desk" />
        </header>

        {copyState !== "idle" && (
          <p role="status" className="desk-status">
            {copyState === "failed"
              ? "คัดลอกไม่สำเร็จ — เปิดข้อความด้านล่างแล้วเลือกคัดลอกด้วยตนเอง · Copy failed; open the text below and copy it manually."
              : "คัดลอกข้อความพร้อมแหล่งที่มาและเวลาอ้างอิงแล้ว · Copied, with sources and reference times."}
          </p>
        )}

        <div className="desk-body">
          {/* The state's four conditions. A strip, because it is a comparison:
              the same four, side by side, each with its own level word. */}
          <DeskStateStrip
            cells={cells}
            actions={actions}
            now={now}
            onOpenEvidence={goToEvidence}
          />

          {loading && (
            <p className="desk-waiting mono">กำลังดึงข่าว… · Fetching the news desk</p>
          )}

          {failed && (
            <p className="desk-waiting desk-waiting--failed">
              ดึงโต๊ะข่าวไม่สำเร็จ — สภาพของเมืองด้านบนยังใช้ได้ตามปกติ · The news desk could not be reached; the conditions above are unaffected.
            </p>
          )}

          {!loading && !failed && !hasStories && (
            <p className="desk-waiting desk-waiting--failed">
              {translation.pending
                ? "กำลังแปลข่าวจากภาษาอื่นในเครื่องนี้… · Translating the other languages in this browser."
                : "วันนี้ยังไม่มีพาดหัวที่อ่านได้ — ดูสิ่งที่โต๊ะนี้แสดงไม่ได้ด้านล่าง · No readable headline this morning; the gaps are named below."}
            </p>
          )}

          {/*
            The census — what the desk covers and what it could not show —
            is rendered when the desk has loaded. When the feed did not
            answer at all, the message above says so once, and a census
            repeating it would be the same fact said twice.
          */}
          {(hasStories || (feedState !== null && desk.gaps.length > 0)) && (
            <div
              className={`desk-bento${
                hasStories
                  ? desk.world.length === 0
                    ? " desk-bento--no-world"
                    : ""
                  : " desk-bento--census-only"
              }`}
            >
              {desk.lead && (
                <section className="desk-compartment desk-lead" aria-label="ข่าวเด่น / Lead story">
                  <p className="desk-compartment-label">ข่าวเด่น · Lead story</p>
                  <StoryLines item={desk.lead} lead />
                  <p className="desk-story-context">
                    {context.th}
                    <span>{context.en}</span>
                  </p>
                  <StoryProvenance item={desk.lead} />
                  <a
                    className="desk-story-link"
                    href={desk.lead.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    อ่านที่ต้นฉบับ · Read at the source ↗
                  </a>
                </section>
              )}

              {desk.world.length > 0 && (
                <section className="desk-compartment desk-world" aria-label="ข่าวจากภาษาอื่น / Coverage in other languages">
                  <p className="desk-compartment-label">ข่าวจากภาษาอื่น · Coverage in other languages</p>
                  {desk.world.map((item) => (
                    <article key={item.id} className="desk-story">
                      <StoryLines item={item} />
                      <StoryProvenance item={item} />
                    </article>
                  ))}
                </section>
              )}

              {desk.coverage.length > 0 && (
                <section className="desk-compartment desk-coverage" aria-label="ข่าวไทยและอังกฤษ / Thai and English coverage">
                  <p className="desk-compartment-label">ข่าวไทยและอังกฤษ · Thai and English coverage</p>
                  {desk.coverage.map((item) => (
                    <article key={item.id} className="desk-story">
                      <StoryLines item={item} />
                      <StoryProvenance item={item} />
                    </article>
                  ))}
                </section>
              )}

              <DeskCensus
                languages={feedState?.languages ?? []}
                machineCount={machineCount}
                gaps={desk.gaps}
                source={feedState?.source ?? "—"}
                ageMinutes={feedState?.ageMinutes ?? 0}
                pending={translation.pending}
              />
            </div>
          )}

          <details className="desk-text">
            <summary>ข้อความสำหรับส่ง LINE · Text that will be copied</summary>
            <pre lang="th">{text.th}</pre>
            <h3>English text</h3>
            <pre lang="en">{text.en}</pre>
          </details>
        </div>
      </div>
    </div>
  );
}

